import React, { useCallback } from 'react';
import {
    ApexInfluencer,
    TiktokUserImage,
    InstagramUserImage,
    CampaignType,
    Platform,
    Currency,
    InfluencerTag,
    ContentTag,
} from '@round/api';
import styles from './RosterInfluencerBar.module.css';
import { ReactComponent as TiktokIcon } from '../../../../../../assets/TiktokIcon.svg';
import { ReactComponent as InstagramIcon } from '../../../../../../assets/InstagramIcon.svg';
import { ReactComponent as TickCircleIcon } from '../../../../../../assets/TickCircle.svg';
import { formatNumberToKNotation } from '../../../../../../utils/valueFormatHelpers';
import { Button, Image } from '@round/ui-kit';
import LocationFlagIcon from '../../../../../../ui/LocationFlag/LocationFlag';
import InfluencerBarStat from '../../../../../influencer/roster/RosterList/components/RosterInfluencerBar/components/InfluencerBarStat/InfluencerBarStat';
import cn from 'classnames';
import TagsWithPopover from './components/TagsWithPopover/TagsWithPopover';
import { buildInstagramUserUrl, buildTiktokUserUrl } from '../../../../../../utils/utits';

type RosterInfluencerBarProps = {
    influencer: ApexInfluencer;
    influencerImage?: TiktokUserImage | InstagramUserImage;
    locationTag?: InfluencerTag;
    contentTags?: ContentTag[];
    campaignType?: CampaignType;
    currency: Currency | undefined;
    readOnly?: boolean;
    bookings?: Platform[];
    addToBooking?: (platform: Platform, influencer: ApexInfluencer) => void;
};

const RosterInfluencerBar = ({
    influencer,
    influencerImage,
    locationTag,
    contentTags,
    campaignType = 'BRAND',
    currency,
    readOnly,
    bookings,
    addToBooking,
}: RosterInfluencerBarProps) => {
    const userName = influencer.tiktok_username || influencer.instagram_username;
    const displayName = influencer.name || influencer.tiktok_display_name || influencer.instagram_display_name;
    const managerEmail =
        campaignType === 'BRAND'
            ? 'partnerships@apexcreators.com'
            : influencer.manager_email ?? 'sophia@apexcreators.com';

    const instagramCost = campaignType === 'BRAND' ? influencer.cost_instagram_brand : influencer.cost_instagram_audio;
    const tiktokCost = campaignType === 'BRAND' ? influencer.cost_tiktok_brand : influencer.cost_tiktok_audio;

    const tiktokFollowers = influencer.tiktok_follower_count
        ? formatNumberToKNotation(influencer.tiktok_follower_count)
        : '-';
    const instagramFollowers = influencer.instagram_follower_count
        ? formatNumberToKNotation(influencer.instagram_follower_count)
        : '-';

    const displayTiktokDetails = typeof tiktokCost === 'number';
    const displayInstagramDetails = typeof instagramCost === 'number';

    const isTiktokBooked = bookings?.includes('TIKTOK');
    const isInstagramBooked = bookings?.includes('INSTAGRAM');

    const handleAddToBooking = useCallback(
        (platform: Platform, influencer: ApexInfluencer) => {
            if (typeof addToBooking === 'function') {
                addToBooking(platform, influencer);
            }
        },
        [addToBooking]
    );

    const tiktokUserLink = influencer.tiktok_username ? buildTiktokUserUrl(influencer.tiktok_username) : null;
    const instagramUserLink = influencer.instagram_username
        ? buildInstagramUserUrl(influencer.instagram_username)
        : null;

    return (
        <div className={styles.barWrapper}>
            <div className={styles.influencerProfile}>
                <Image
                    src={influencerImage?.avatar_thumb.cached_url}
                    alt="Influencer"
                    className={styles.influencerImage}
                    skeletonClassName={styles.imageSkeleton}
                />
                <div className={styles.influencerInfo}>
                    <div>
                        <p className={styles.displayName}>{displayName}</p>
                        <p className={styles.userName}>{`@${userName}`}</p>
                    </div>
                    <div className={styles.locationRow}>
                        <LocationFlagIcon
                            className={styles.locationFlag}
                            locationCode={locationTag?.code ? locationTag.code : null}
                        />
                        <span className={styles.influencerSubDetail}>{locationTag ? locationTag.name : 'N/A'}</span>
                    </div>
                    <span className={styles.influencerSubDetail}>{managerEmail}</span>
                </div>
            </div>
            <div className={styles.influencerStats}>
                {displayTiktokDetails && (
                    <div className={styles.platformColumn}>
                        <InfluencerBarStat
                            Icon={TiktokIcon}
                            followers={tiktokFollowers}
                            cost={`${currency?.symbol}${tiktokCost}`}
                            campaignType={campaignType}
                            onIconClick={tiktokUserLink ? () => window.open(tiktokUserLink, '_blank') : undefined}
                        />
                        {!readOnly && (
                            <Button
                                type={isTiktokBooked ? 'bordered' : 'filled'}
                                color="black"
                                className={cn(styles.addToBookingButton, {
                                    [styles.isAdded]: isTiktokBooked,
                                })}
                                onClick={() => handleAddToBooking('TIKTOK', influencer)}
                            >
                                {isTiktokBooked ? (
                                    <>
                                        <TickCircleIcon className={styles.tickIcon} />
                                        <span>Added to booking</span>
                                    </>
                                ) : (
                                    <span>Add Tiktok post to booking</span>
                                )}
                            </Button>
                        )}
                    </div>
                )}
                {displayInstagramDetails && (
                    <div className={styles.platformColumn}>
                        <InfluencerBarStat
                            Icon={InstagramIcon}
                            followers={instagramFollowers}
                            cost={`${currency?.symbol}${instagramCost}`}
                            campaignType={campaignType}
                            onIconClick={instagramUserLink ? () => window.open(instagramUserLink, '_blank') : undefined}
                        />
                        {!readOnly && (
                            <Button
                                type={isInstagramBooked ? 'bordered' : 'filled'}
                                color="black"
                                className={cn(styles.addToBookingButton, {
                                    [styles.isAdded]: isInstagramBooked,
                                })}
                                onClick={() => handleAddToBooking('INSTAGRAM', influencer)}
                            >
                                {isInstagramBooked ? (
                                    <>
                                        <TickCircleIcon className={styles.tickIcon} />
                                        <span>Added to booking</span>
                                    </>
                                ) : (
                                    <span>Add Instagram post to booking</span>
                                )}
                            </Button>
                        )}
                    </div>
                )}
            </div>

            <div className={styles.tagsSection}>
                <TagsWithPopover tags={contentTags} containerClassName={styles.tagsContainer} />
            </div>
        </div>
    );
};

export default RosterInfluencerBar;
